import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { formatDate, getLeaveTypeName, getStatusBadge } from '../lib/utils';
import { CheckCircle, XCircle } from 'lucide-react';

export const LeaveRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      const response = await api.get(`/leave/requests?status=${filter}`);
      setRequests(response.data.data.requests);
    } catch (error) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await api.patch(`/leave/requests/${requestId}/approve`, { comments: 'Approved' });
      toast.success('Leave request approved');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (requestId) => {
    try {
      await api.patch(`/leave/requests/${requestId}/reject`, { reason: 'Rejected' });
      toast.success('Leave request rejected');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  return (
    <div className="space-y-6" data-testid="requests-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-heading" data-testid="requests-title">Leave Requests</h1>
        <p className="text-slate-600 mt-1">Review and manage leave requests</p>
      </div>

      <div className="flex gap-2" data-testid="filter-buttons">
        <Button
          variant={filter === 'pending' ? 'default' : 'outline'}
          onClick={() => setFilter('pending')}
          data-testid="filter-pending"
        >
          Pending
        </Button>
        <Button
          variant={filter === 'approved' ? 'default' : 'outline'}
          onClick={() => setFilter('approved')}
          data-testid="filter-approved"
        >
          Approved
        </Button>
        <Button
          variant={filter === 'rejected' ? 'default' : 'outline'}
          onClick={() => setFilter('rejected')}
          data-testid="filter-rejected"
        >
          Rejected
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 mt-6" data-testid="requests-loading"><p className="text-slate-600">Loading...</p></div>
      ) : requests.length === 0 ? (
        <Card className="mt-6" data-testid="requests-empty"><CardContent className="py-12 text-center"><p className="text-slate-600">No {filter} requests found.</p></CardContent></Card>
      ) : (
        <div className="space-y-4 mt-6" data-testid="requests-list">
          {requests.map((request) => (
            <Card key={request.request_id} className="leave-request-card" data-testid={`request-${request.request_id}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900">{getLeaveTypeName(request.leave_type)}</h3>
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getStatusBadge(request.status)}`}>
                        {request.status.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-slate-700 mb-2">{request.user?.full_name} - {request.user?.department}</p>
                    <div className="space-y-1 text-sm text-slate-600">
                      <p>{formatDate(request.start_date)} - {formatDate(request.end_date)} ({request.total_days} days)</p>
                      <p><strong>Reason:</strong> {request.reason}</p>
                    </div>
                  </div>
                  {request.status === 'pending' && (
                    <div className="flex gap-2" data-testid="request-actions">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg" onClick={() => handleApprove(request.request_id)} data-testid="approve-btn">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-400 border-red-400/30 hover:bg-red-500/10 rounded-lg" onClick={() => handleReject(request.request_id)} data-testid="reject-btn">
                        <XCircle className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeaveRequests;
